import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import { formatWithCountryCode, getRoleLabel } from '../../lib/utils';
import CustomAvatar from '../elements/CustomAvatar';
import ProfileDetails from './ProfileDetails';

const InfoItem = ({ label, value, isLink = false }) => {
  if (!value) return null;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>

      {isLink ? (
        <Link
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {value}
        </Link>
      ) : (
        <Typography variant="body2">{value}</Typography>
      )}
    </Box>
  );
};

const SectionTitle = ({ children }) => (
  <Grid item xs={12}>
    <Typography variant="overline" color="text.secondary">
      {children}
    </Typography>
  </Grid>
);

const AccountProfile = ({ user, fetchUserData }) => {
  if (!user) return null;

  const {
    first_name,
    last_name,
    email,
    phone_number,
    country_code,
    role,
    organization_name,
    industry,
    org_phone_number,
    org_country_code,
    website,
    linkedin,
    evaluation_reason,
  } = user;

  const fullName = [first_name?.trim(), last_name?.trim()]
    .filter(Boolean)
    .join(' ');

  const showOrg = role !== 'super-admin';

  const orgFields = [
    organization_name && {
      label: 'Name',
      value: organization_name,
    },
    industry && {
      label: 'Industry',
      value: industry,
    },
    org_phone_number && {
      label: 'Phone',
      value: formatWithCountryCode(org_phone_number, org_country_code),
    },
    website && {
      label: 'Website',
      value: website,
      isLink: true,
    },
    linkedin && {
      label: 'LinkedIn',
      value: linkedin,
      isLink: true,
    },
  ].filter(Boolean);

  return (
    <Card sx={{ position: 'relative' }}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="stretch">
          {/* LEFT: Avatar */}
          <Grid
            item
            xs={12}
            sm={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}>
            <CustomAvatar
              firstName={first_name}
              lastName={last_name}
              showTitle={false}
              size={88}
            />

            <Box textAlign="center">
              <Typography variant="h6">{fullName}</Typography>

              <Chip
                label={getRoleLabel(role)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Grid>

          {/* Divider */}
          <Grid item xs="auto" sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Divider orientation="vertical" flexItem />
          </Grid>

          {/* RIGHT: Details */}
          <Grid item xs={12} sm>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <SectionTitle>Personal</SectionTitle>

              <Grid item xs={12} sm={6}>
                <InfoItem label="Email" value={email} />
              </Grid>

              {phone_number && (
                <Grid item xs={12} sm={6}>
                  <InfoItem
                    label="Phone"
                    value={formatWithCountryCode(phone_number, country_code)}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={1}>
              {showOrg && (
                <>
                  <SectionTitle>Organization</SectionTitle>

                  {orgFields.map((field) => (
                    <Grid key={field.label} item xs={12} sm={6}>
                      <InfoItem {...field} />
                    </Grid>
                  ))}

                  {evaluation_reason && (
                    <Grid item xs={12}>
                      <InfoItem label="Why GAiNSS" value={evaluation_reason} />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>

          <ProfileDetails user={user} fetchUserData={fetchUserData} />
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AccountProfile;
